/**
 * The workspace's TASK section — one lane-v2 task (R3) in the right-hand panel, and the exact bytes
 * of one of its work items (R4) when the reader asks for them.
 *
 * READS THE SESSION STORE, NOT AN ENDPOINT. `laneV2SessionStore.snapshots[taskId]` is the R3
 * snapshot the stream keeps current (frames → refetch); this section renders it and never fetches a
 * task itself. The one fetch it makes is R4 (`loadItemBody`), which the read model serves on demand
 * and no snapshot carries — list → detail → Back, the same shape as the changeset board's file diff.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { LaneV2TaskBlock } from "../laneV2/LaneV2TaskBlock";
import { ItemDiffView } from "../laneV2/ItemDiffView";
import { pickDefaultFile, viewableItems } from "../laneV2/changeTarget";
import { describeLaneV2Error, isLaneV2Error } from "@/services/multi-agent/laneV2/api";
import type { ItemBody } from "@/services/multi-agent/laneV2/types";
import { useLaneV2SessionStore } from "@/stores/laneV2SessionStore";
import { selectWorkspaceTaskItem, useWeaveWorkspaceStore } from "@/stores/weaveWorkspaceStore";

interface ItemBodyState {
  loading: boolean;
  body: ItemBody | null;
  error: string | null;
}

/**
 * What the section can say about the task it was asked for. `loading` is the attach still reading the session
 * (`streamStatus: "connecting"`): the snapshot is not in the store YET, which is not the same as the task being gone.
 */
export function taskAvailability(taskId: string | null, snapshot: unknown, streamStatus: string): "ready" | "loading" | "failed" | "gone" {
  if (taskId && snapshot) return "ready";
  if (!taskId) return "gone";
  // A failed attach is not a missing task: say the task could not be loaded, never "no longer has that task".
  if (streamStatus === "connecting") return "loading";
  return streamStatus === "disconnected" ? "failed" : "gone";
}

export function LaneV2TaskSection({ taskId }: { taskId: string | null }) {
  const snapshot = useLaneV2SessionStore((s) => (taskId ? s.snapshots[taskId] ?? null : null));
  // The attach is still reading the session: a task that is not in the store YET is not a task that is gone.
  const streamStatus = useLaneV2SessionStore((s) => s.streamStatus);
  const loadError = useLaneV2SessionStore((s) => s.error);
  const workItemId = useWeaveWorkspaceStore(selectWorkspaceTaskItem);
  const openTask = useWeaveWorkspaceStore((s) => s.openTask);
  const [item, setItem] = useState<ItemBodyState | null>(null);
  const changed = useMemo(() => (snapshot ? viewableItems(snapshot) : []), [snapshot]);

  /*
   * R4 on demand, keyed on the item the store names. The result is component state, not store
   * state: it is one reader's open detail view, and closing it (Back, another section) should not
   * leave a body cached anywhere a later open could mistake for fresh.
   */
  useEffect(() => {
    if (!taskId || !workItemId) {
      setItem(null);
      return;
    }
    let cancelled = false;
    setItem({ loading: true, body: null, error: null });
    useLaneV2SessionStore
      .getState()
      .loadItemBody(taskId, workItemId)
      .then((body) => {
        if (!cancelled) setItem({ loading: false, body, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setItem({ loading: false, body: null, error: isLaneV2Error(err) ? describeLaneV2Error(err) : "Could not load this item." });
      });
    return () => {
      cancelled = true;
    };
  }, [taskId, workItemId]);

  const availability = taskAvailability(taskId, snapshot, streamStatus);
  if (availability === "failed") {
    return (
      <p role="alert" data-lane-v2-task-load-failed="" className="px-1 py-6 text-center text-[13px] text-muted-foreground">
        {loadError ?? "Couldn\u2019t load this task."}
      </p>
    );
  }
  if (availability === "loading") {
    return (
      <p role="status" data-lane-v2-task-loading="" className="px-1 py-6 text-center text-[13px] text-muted-foreground">
        Loading…
      </p>
    );
  }
  if (!taskId || !snapshot) {
    return <p className="px-1 py-6 text-center text-[13px] text-muted-foreground">This conversation no longer has that task.</p>;
  }

  if (workItemId) {
    const at = changed.findIndex((c) => c.workItemId === workItemId);
    const go = (i: number) => openTask(taskId, changed[i].workItemId);
    return (
      <div className="flex flex-col gap-2" data-lane-v2-task-item={workItemId}>
        {item?.loading ? (
          <>
            <BackToChanges onClick={() => openTask(taskId, null)} />
            <p className="text-[13px] text-muted-foreground">Loading…</p>
          </>
        ) : item?.error ? (
          <>
            <BackToChanges onClick={() => openTask(taskId, null)} />
            <p role="alert" className="text-[13px] text-ma-err-text">
              {item.error}
            </p>
          </>
        ) : item?.body ? (
          // Keyed by item so a different item re-seeds the landing file; View lands on the changed code.
          <ItemDiffView
            key={item.body.work_item_id}
            body={item.body}
            initialPath={pickDefaultFile(item.body)}
            nav={{
              onChanges: () => openTask(taskId, null),
              position: at >= 0 ? { index: at + 1, total: changed.length } : undefined,
              onPrev: at > 0 ? () => go(at - 1) : undefined,
              onNext: at >= 0 && at < changed.length - 1 ? () => go(at + 1) : undefined,
            }}
          />
        ) : null}
      </div>
    );
  }

  const store = useLaneV2SessionStore.getState;
  return (
    <LaneV2TaskBlock
      snapshot={snapshot}
      onCancelTask={(id) => store().cancelTask(id)}
      onRevertTask={(id) => store().revertTask(id)}
      onViewItemBody={(id, itemId) => openTask(id, itemId)}
    />
  );
}

/** The one back level while a diff is loading or failed (the loaded diff carries the breadcrumb instead). */
function BackToChanges({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex w-fit items-center gap-1 rounded-md px-1.5 py-1 text-[13px] font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
      Changes
    </button>
  );
}
